<template>
  <div
    class="code-color-indicator"
    :class="[props.codeType, props.isCompact ? 'compact' : 'regular']"
  >
    <PsPopover
      placement="right"
      trigger="hover"
    >
      <ul class="color-list">
        <li
          v-for="code in visibleCodes"
          :key="code.id"
          class="color-list__item"
          :style="{ background: codeColors[code.id] }"
        />
        <li
          v-if="
            (!isFeedsIndicator || isCompact) &&
              visibleCodes.length < props.codes.length
          "
          class="color-list__more"
        >
          +{{ props.codes.length - visibleCodes.length }}
        </li>
      </ul>
      <template #body>
        <div
          class="code-color-indicator-details"
          :class="codeType"
        >
          <ul class="color-list-full">
            <li
              v-for="code in props.codes"
              :key="code.id"
              class="color-list-full__item"
            >
              <span
                class="color-list-full__color"
                :style="{ background: codeColors[code.id] }"
              />
              <span>{{ code.name }}</span>
            </li>
          </ul>
        </div>
      </template>
    </PsPopover>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import PsPopover from '@/ContextTab/components/UI/Popover/PsPopover.vue'
import { useCodesStore } from '@/ContextTab/stores/codes'
import type { CodeFilterTypes } from '@/ContextTab/modules/news/codes/types'
import type { CodeWithCategory } from '@/api/generated/v2'


const props = defineProps<{
  codeType: CodeFilterTypes
  codes: CodeWithCategory[]
  maxItems: number
  isCompact: boolean
}>()

const codesStore = useCodesStore()

const isFeedsIndicator = computed(() => props.codeType === 'feeds')
const visibleCodes = computed(() => {
  let lastColor = 'transparent'
  const res: CodeWithCategory[] = []

  // preserve max different COLORS (as sibling codes may be of same color)
  for (let code of props.codes) {
    const color = codeColors.value[code.id]

    if (color !== lastColor) {
      res.push(code)

      if (res.length >= props.maxItems) {
        break
      }

      lastColor = color
    }
  }

  return res
})

const codeColors = computed(() => props.codes
  .reduce((acc, { id }) => {
    acc[id] = codesStore.codeColorsMap[id] ?? 'transparent'
    return acc
  }, {} as Record<number, string>),
)
</script>

<style scoped>
.code-color-indicator {

  /* 4 variants: feeds/non-feeds x compact/regular */

  &.regular {
    &.feeds {
      position: absolute;
      z-index: 20;
      top: 0;
      left: 0;
      height: 100%;

      .color-list {
        display: flex;
        flex-direction: column;
        height: 100%;
        width: 5px;
      }

      .color-list__item {
        flex: 1;
      }
    }
  }

  &.regular, &.compact {
    &.non-feeds {
      z-index: 2; /* over list-item-actions */

      .color-list {
        display: flex;
        align-items: center;
        gap: 4px;
      }

      .color-list__item {
        flex: 0 0 10px;
        height: 10px;
        border-radius: 50%;
      }
    }
  }

  &.compact {
    &.feeds {
      height: 100%;

      .color-list {
        display: flex;
        align-items: center;
        height: 100%;
      }

      .color-list__item {
        display: inline-block;
        flex: 0 0 5px;
        height: 100%;
      }

      .color-list__more {
        margin-left: 4px;
      }
    }
  }

  .color-list, .color-list-full {
    /* TODO: replace with tokens */
    font-size: 13px;
    font-weight: bold;
    color: #9d9d9d;
  }
}

.code-color-indicator-details {
  .color-list-full {
    display: block;
  }

  .color-list-full__item {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &.feeds {
    .color-list-full__color {
      height: 20px;
      flex: 0 0 5px;
    }
  }

  &.non-feeds {
    .color-list-full__color {
      flex: 0 0 10px;
      height: 10px;
      border-radius: 50%;
    }
  }
}

:deep(.pskit__popover) {
  height: 100%;
}
:deep(.pskit__popover__trigger) {
  height: 100%;
}
</style>
